// The history page (spec/dashboard.md section 12.1, R-BR-02): the commit list beside the branch graph, the selected commit
// (`history/{sha}`), Compare (`?from=&to=`), Validate branch (`?validate=1`, the job form), New branch… and Branch settings….
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { ops } from '../../api/operations';
import type { Job, ValidationReport as ValidationReportData } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ValidationReport } from '../../history/ValidationReport';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { JobProgress } from '../../ui/JobProgress';
import { Modal, useUrlParam } from '../../ui/Modal';
import { BranchSettingsDialog, NewBranchDialog } from './BranchDialogs';
import { CommitDetail } from './CommitDetail';
import { CommitLog } from './CommitLog';
import { ComparePanel } from './ComparePanel';
import { errorText, shortSha } from './util';
import './history-page.css';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import { ArrowRightLeft, ClipboardCheck, GitBranch, RefreshCw, Settings } from '../../ui/icons';

export function HistoryPage() {
  const { branch = '', sha = null } = useParams();
  const { org, game, branches, missing } = useGame();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const validate = useUrlParam('validate');
  const current = branches.find((b) => b.name === branch);
  const urlCompare = params.has('from') || params.has('to');
  const [compareOpen, setCompareOpen] = useState(urlCompare);
  useEffect(() => {
    if (urlCompare) setCompareOpen(true);
  }, [urlCompare]);
  const [newBranch, setNewBranch] = useState<{ from: string | null } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (branches.length > 0 && !current) {
    return (
      <section className="history-page" data-testid="history">
        <div className="page-head">
          <h1>History</h1>
        </div>
        <EmptyState icon={<GitBranch />} title={`No branch “${branch}”`} body="Pick a branch with the branch selector." />
      </section>
    );
  }

  const select = (s: string) => navigate({ pathname: routes.history(org, game.slug, branch, s), search: params.toString() ? `?${params.toString()}` : '' });
  const closeCompare = () => {
    setCompareOpen(false);
    if (urlCompare) {
      const next = new URLSearchParams(params);
      next.delete('from');
      next.delete('to');
      setParams(next);
    }
  };
  return (
    <section className="history-page" data-testid="history">
      <div className="page-head">
        <h1>History</h1>
        <span className="page-meta">
          <GitBranch className="icon-s" aria-hidden="true" />
          <code>{branch}</code>
        </span>
        {current?.isDefault && <Badge tone="accent">default</Badge>}
        {current?.isProtected && <Badge tone="warning">protected</Badge>}
        {current?.head && (
          <span className="page-meta" title={current.head}>
            head <code>{shortSha(current.head)}</code>
          </span>
        )}
        <span className="spacer" />
        <div className="row history-toolbar" role="toolbar" aria-label="History">
          <Button size="s" icon={<ArrowRightLeft aria-hidden="true" />} onClick={() => (compareOpen ? closeCompare() : setCompareOpen(true))} aria-pressed={compareOpen}>
            Compare…
          </Button>
          <Button size="s" icon={<ClipboardCheck aria-hidden="true" />} onClick={() => validate.open('1')}>
            Validate branch
          </Button>
          <Button size="s" icon={<Settings aria-hidden="true" />} onClick={() => setSettingsOpen(true)} disabled={!current}>
            Branch settings…
          </Button>
          <Button size="s" icon={<GitBranch aria-hidden="true" />} onClick={() => setNewBranch({ from: null })} missingPermission={missing('branch_create')}>
            New branch…
          </Button>
        </div>
      </div>
      {compareOpen && <ComparePanel branch={branch} onClose={closeCompare} />}
      <div className={`history-body${sha ? ' with-detail' : ''}`}>
        <CommitLog branch={branch} selected={sha} onSelect={select} />
        {sha && <CommitDetail key={sha} branch={branch} sha={sha} onCreateBranch={(s) => setNewBranch({ from: s })} onClose={() => navigate(routes.history(org, game.slug, branch))} />}
      </div>
      <Modal title={`Validate ${branch}`} open={validate.value !== null} onClose={validate.close} drawer size="l">
        {validate.value !== null && <ValidateBody branch={branch} />}
      </Modal>
      <NewBranchDialog open={newBranch !== null} onClose={() => setNewBranch(null)} branch={branch} fromSha={newBranch?.from ?? null} />
      <BranchSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} branch={current} />
    </section>
  );
}

/**
 * `POST …:validate { async: true }` on open and on Run again (spec/api-conventions.md section 21.5): the `branch.validate`
 * job is followed with JobProgress and its result — the same report as the synchronous answer — is rendered. Advisory: the
 * release build runs the same validators and is the gate.
 */
function ValidateBody({ branch }: { branch: string }) {
  const { client } = useAuth();
  const { org, game, missing } = useGame();
  const [run, setRun] = useState(0);
  const [state, setState] = useState<{ loading: boolean; error: string | null; report: ValidationReportData | null; job: Job | null }>({ loading: true, error: null, report: null, job: null });
  useEffect(() => {
    let live = true;
    setState({ loading: true, error: null, report: null, job: null });
    client
      .data(ops.validateBranch, { path: { org, game: game.slug, branch }, body: { async: true } })
      .then((r) => live && setState({ loading: true, error: null, report: null, job: r as unknown as Job }))
      .catch((e: unknown) => live && setState({ loading: false, error: errorText(e), report: null, job: null }));
    return () => {
      live = false;
    };
  }, [run, client, org, game.slug, branch]);
  const onDone = useCallback((job: Job) => {
    if (job.status === 'succeeded') setState({ loading: false, error: null, report: job.result as unknown as ValidationReportData, job });
    else {
      const e = job.error as { code?: string; message?: string } | null;
      setState({ loading: false, error: `Validation ${job.status}${e?.message ? `: ${e.message}` : ''}`, report: null, job });
    }
  }, []);
  const onRetry = useCallback((job: Job) => setState({ loading: true, error: null, report: null, job }), []);
  const jobApi = useMemo(() => ({ client, org, game: game.slug, missingPermission: missing('content_write') }), [client, org, game.slug, missing]);
  const r = state.report;
  return (
    <div className="validate-drawer" data-testid="validate-drawer">
      <div className="page-toolbar">
        <span className="muted small">Advisory: the release build runs the same validators and is the gate.</span>
        <span className="spacer" />
        <Button size="s" icon={<RefreshCw aria-hidden="true" />} onClick={() => setRun((n) => n + 1)} busy={state.loading}>
          Run again
        </Button>
      </div>
      {state.job && <JobProgress job={state.job} api={jobApi} onDone={onDone} onRetry={onRetry} />}
      {state.error ? (
        <LoadError>{state.error}</LoadError>
      ) : !r ? (
        <SkeletonRows rows={3} label="Validating" />
      ) : (
        <ValidationReport issues={r.issues} branch={branch} commit={r.commit} ok={r.ok} errors={r.errors} warnings={r.warnings} />
      )}
    </div>
  );
}
